import React, {useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ChevronDownCheckout, ChevronUpCheckout} from '../../../../components/icons'
import {generateStyles, getHideStyles} from '../../utils/utils'
import {renderComponentsSwitch} from '../../utils/contentRenderer'

const TitleDescriptionXHtmlBlock = ({
    Title,
    BulletItems,
    BulletItemStyle,
    IsCollapsible,
    CollapsibleIcon,
    BackgroundColor,
    TitleDescriptionXHtmlBlockTitleStyle,
    overwriteXHtmlBlockTitleStyle,
    overwriteXHtmlBlockCollapsibleWrapperStyle,
    overwriteXHtmlBlockBulletWrapperStyle,
    HideOnDevices,
    isTabOpen = false,
    ...props
}) => {
    const hideStyles = getHideStyles(HideOnDevices)

    if (hideStyles.doNotLoadComponent) return

    const [isOpen, setIsOpen] = useState(isTabOpen)
    const contentRef = useRef(null)
    const styles = useMultiStyleConfig('TitleDescriptionXHtmlBlock', {...props})

    // Decode HTML entities
    const decodeHtmlEntities = (html) => {
        if (!html) return ''

        // Basic HTML entity decoding for common cases
        let decoded = html
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&nbsp;/g, ' ')

        // Remove <br> tags adjacent to table structure elements (caused by
        // CMS content using <br> as line separators in escaped table markup)
        decoded = decoded.replace(
            /<br\s*\/?>\s*(?=<\/?(?:table|thead|tbody|tfoot|tr|th|td)[\s>])/gi,
            ''
        )
        decoded = decoded.replace(
            /(<\/?(?:table|thead|tbody|tfoot|tr|th|td)[^>]*>)\s*<br\s*\/?>/gi,
            '$1'
        )

        return decoded
    }

    const decodedBulletItems = decodeHtmlEntities(BulletItems)

    if (IsCollapsible) {
        return (
            <Box {...styles.collapsibleWrapper} {...overwriteXHtmlBlockCollapsibleWrapperStyle} {...hideStyles} data-block-type="TitleDescriptionXHtmlBlock">
                <Text
                    onClick={() => setIsOpen(!isOpen)}
                    onKeyDown={(e) => e.key === 'Enter' && setIsOpen(!isOpen)}
                    {...generateStyles(TitleDescriptionXHtmlBlockTitleStyle, {
                        ...styles.collapsibleTitle,
                        ...overwriteXHtmlBlockTitleStyle
                    })}
                    role="button"
                    tabIndex={0}
                    aria-label={Title}
                    sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    {Title}
                    <Box
                        sx={{
                            transition: 'transform 0.2s linear',
                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                            display: 'flex',
                            alignItems: 'center'
                        }}
                    >
                        {isOpen ? <ChevronUpCheckout /> : <ChevronDownCheckout />}
                    </Box>
                </Text>
                <Box
                    ref={contentRef}
                    sx={{
                        overflow: 'hidden',
                        transition: 'max-height 0.2s linear',
                        maxHeight: isOpen ? `${contentRef.current?.scrollHeight || 0}px` : '0px'
                    }}
                >
                    <Box
                        fontSize="sm"
                        mt={2}
                        pb={4}
                        sx={{
                            '& ul': {
                                paddingLeft: '20px',
                                margin: '16px 0'
                            },
                            '& h4': {
                                fontWeight: 'bold !important',
                                marginBottom: 4
                            },
                            '& h4:not(:first-child)': {
                                marginTop: 4
                            }
                        }}
                        {...overwriteXHtmlBlockBulletWrapperStyle}
                    >
                        <Box
                            {...generateStyles(BulletItemStyle)}
                            sx={{
                                '& a': {
                                    textDecoration: 'underline'
                                },
                                '& table': {
                                    marginTop: '1em'
                                }
                            }}
                            dangerouslySetInnerHTML={{__html: decodedBulletItems}}
                        />
                    </Box>
                </Box>
            </Box>
        )
    }

    const hasBackground = BackgroundColor && BackgroundColor !== ''
    const bulletIcon = CollapsibleIcon?.Expanded

    return (
        <Box
            {...styles.wrapper}
            backgroundColor={hasBackground ? BackgroundColor : 'transparent'}
            paddingX={hasBackground ? '32px' : 0}
            paddingY={hasBackground ? '16px' : 0}
            marginBottom={hasBackground ? 2 : 12}
            sx={{
                '& ul': {
                    paddingLeft: '20px',
                    margin: '16px 0'
                },
                '& ol': {
                    paddingLeft: '20px',
                    margin: '16px 0'
                },
                '& h4': {
                    fontWeight: 'bold !important',
                    marginBottom: 4
                },
                '& h4:not(:first-child)': {
                    marginTop: 4
                }
            }}
            {...hideStyles}
            data-block-type="TitleDescriptionXHtmlBlock"
        >
            {Title && (
                <Text
                    as={TitleDescriptionXHtmlBlockTitleStyle?.TextSize}
                    {...generateStyles(TitleDescriptionXHtmlBlockTitleStyle, {
                        ...styles[TitleDescriptionXHtmlBlockTitleStyle?.TextSize],
                        ...overwriteXHtmlBlockTitleStyle
                    })}
                >
                    {Title}
                </Text>
            )}
            {bulletIcon ? (
                <Box display="flex" alignItems="center" gap={6}>
                    <Box flexShrink={0} width="24px" height="24px">
                        {renderComponentsSwitch(bulletIcon, {
                            customMediaStyling: {width: '24px', height: '24px'}
                        })}
                    </Box>
                    <Box
                        {...generateStyles(BulletItemStyle, overwriteXHtmlBlockBulletWrapperStyle)}
                        dangerouslySetInnerHTML={{__html: decodedBulletItems}}
                        sx={{
                            '& p': {
                                margin: 0
                            },
                            '& a': {
                                textDecoration: 'underline'
                            },
                            '& table': {
                                marginTop: '1em'
                            }
                        }}
                    />
                </Box>
            ) : (
                <Box
                    {...generateStyles(BulletItemStyle, overwriteXHtmlBlockBulletWrapperStyle)}
                    sx={{
                        '& a': {
                            textDecoration: 'underline'
                        },
                        '& table': {
                            marginTop: '1em'
                        }
                    }}
                    dangerouslySetInnerHTML={{__html: decodedBulletItems}}
                />
            )}
        </Box>
    )
}

TitleDescriptionXHtmlBlock.propTypes = {
    Title: PropTypes.string,
    BulletItems: PropTypes.string,
    BulletItemStyle: PropTypes.object,
    IsCollapsible: PropTypes.bool,
    CollapsibleIcon: PropTypes.object,
    BackgroundColor: PropTypes.string,
    TitleDescriptionXHtmlBlockTitleStyle: PropTypes.shape({
        TextSize: PropTypes.string
    }),
    overwriteXHtmlBlockTitleStyle: PropTypes.object,
    overwriteXHtmlBlockCollapsibleWrapperStyle: PropTypes.object,
    overwriteXHtmlBlockBulletWrapperStyle: PropTypes.object,
    HideOnDevices: PropTypes.string
}

export default TitleDescriptionXHtmlBlock
